import React, { FormEvent, useEffect, useState } from 'react'
import { addMonths } from 'date-fns'
import { Modal } from '@entur/modal'
import { GridContainer, GridItem } from '@entur/grid'
import { space } from '@entur/tokens'
import { TextField, VariantType } from '@entur/form'
import { Dropdown } from '@entur/dropdown'
import { PrimaryButton, SecondaryButton } from '@entur/button'
import { BannerAlertBox, SmallAlertBox, useToast } from '@entur/alert'

import { Employee, FetchState, FipTicket } from '../../api-clients/api-types'
import { useApi } from '../../api-clients/client-hooks'
import { patchFIPTickets, postFIPTickets } from '../../api-clients/eba-clients'
import { tolLocalDateFormat, toPrintFormat } from '../../utils/date-utils'
import ValidatableDatePicker from '../form/validatable-date-picker'
import { formIsValid, validateAllFields, ValidationFormField } from '../../utils/form-utils'
import { ValidatableTextField } from '../form/validatable-text-field'
import { useEmployee } from '../../contexts/employee-context'
import TicketRightHoldersDropDown, { ticketRightHolderFormField } from '../form/ticket-right-holders-dropdown'
import { FIPTicketTypeDropdown } from '../form/dropdowns'

type State = {
  ticketRightHolder: ValidationFormField<string>
  ticketType: ValidationFormField<string | undefined>
  issueDate: ValidationFormField<Date | undefined>
  ticketNo: ValidationFormField<string>
  travelType: ValidationFormField<string | undefined>
  validFrom: ValidationFormField<Date | undefined>
  validTo: ValidationFormField<Date | undefined>
  note: ValidationFormField<string>
  [key: string]: unknown
}

const body = (state: State, employeeId: string) => {
  return {
    fipTicketTypeId: state.ticketType.value ? parseInt(state.ticketType.value, 10) : null,
    issueDate: tolLocalDateFormat(state.issueDate.value || new Date()),
    ticketNo: parseInt(state.ticketNo.value, 10),
    travelTypeId: parseInt(state.travelType.value || '', 10),
    validFrom: tolLocalDateFormat(state.validFrom.value || new Date()),
    validUntil: tolLocalDateFormat(state.validTo.value || new Date()),
    note: state.note.value,
    familyMemberId: state.ticketRightHolder.value !== employeeId ? state.ticketRightHolder.value : null,
  }
}

function initialState(employee: Employee, fipTicket?: FipTicket): State {
  const ticketRightHolderId = fipTicket?.familyMemberId || employee.id
  const name = fipTicket?.firstName || employee.firstName
  return {
    ticketRightHolder: ticketRightHolderFormField(ticketRightHolderId, name),
    ticketType: {
      variant: '',
      feedback: '',
      validation: {
        required: true,
      },
      value: fipTicket?.fipTicketTypeSimple?.id?.toString() || '',
      fieldName: 'ticketType',
      displayName: 'Billettype',
    },
    issueDate: {
      variant: '',
      feedback: '',
      validation: {
        required: true,
      },
      value: new Date(fipTicket?.issueDate || new Date()),
      fieldName: 'issueDate',
      displayName: 'Utstedtdato',
    },
    ticketNo: {
      value: fipTicket?.ticketNo?.toString() || '',
      variant: '',
      feedback: '',
      fieldName: 'ticketNo',
      displayName: 'Billettnummer',
      validation: {
        pattern: '^[0-9]*$',
        errorMessage: 'Billettnummer kan kun være tall',
        required: true,
      },
    },
    travelType: {
      variant: '',
      feedback: '',
      validation: {
        required: true,
      },
      value: fipTicket?.travelType?.id ? fipTicket.travelType.id.toString() : '',
      fieldName: 'travelType',
      displayName: 'Reisetype',
    },
    validFrom: {
      variant: '',
      feedback: '',
      validation: {
        required: true,
      },
      value: fipTicket?.validFrom && new Date(fipTicket?.validFrom),
      fieldName: 'validFrom',
      displayName: 'Gyldig fra',
    },
    validTo: {
      variant: '',
      feedback: '',
      validation: {
        required: true,
      },
      value: fipTicket?.validUntil && new Date(fipTicket?.validUntil),
      fieldName: 'validTo',
      displayName: 'Gyldig til',
    },
    note: {
      value: fipTicket?.note || '',
      feedback: '',
      variant: '',
      fieldName: 'note',
      validation: {
        required: false,
      },
      displayName: 'Kommentar',
    },
  }
}

const getEndOfThisOrNextYear = () => {
  // The personnel ticket office wants FIPm vouchers to be
  // pre-filled with the last day of the current year, or
  // next year if created after November
  // https://entur.slack.com/archives/C02PGFSA8MA/p1754554704988279
  const endOfYear = new Date()
  endOfYear.setMonth(11, 31)

  if (new Date().getMonth() >= 10) {
    endOfYear.setFullYear(endOfYear.getFullYear() + 1)
  }

  return endOfYear
}

const travelTypeItems = [
  { label: 'Privatreise (Ansatt)', value: '2' },
  { label: 'Tjenestereise', value: '1' },
  { label: 'Privatreise (Pensjonist)', value: '3' },
]
const TJENESTEREISE = '1'

// Ticket type has no separate field for class, but the code ends with 1 for 1st class and 2 for 2nd class
// (e.g. A1, D1, DSB1 = 1st class, SBB2, DSB2 = 2nd class). The code is extracted from the dropdown label,
// which is in the format "{code} - {description}".
const selectedTicketTypeCode = (state: State): string | undefined =>
  state.ticketType.displayValue?.split(' - ')[0]?.trim()

const isFirstClass = (code: string | undefined): boolean => !!code && code.endsWith('1')
const isSecondClass = (code: string | undefined): boolean => !!code && code.endsWith('2')
const isDsb = (code: string | undefined): boolean => !!code && code.startsWith('DSB')

type FipModalProps = {
  fipTicket?: FipTicket
  fipTickets: FipTicket[]
  open?: boolean
  close: (newTicket?: FipTicket) => void
}
const FipModal: React.FC<FipModalProps> = (props) => {
  const { employee } = useEmployee()
  const { addToast } = useToast()
  const [state, setState] = useState<State>(initialState(employee, props.fipTicket))
  const [duplicateTicketNo, setDuplicateTicketNo] = useState('')
  const [triedToSave, setTriedToSave] = useState(false)

  // Reset on open
  useEffect(() => {
    setState(initialState(employee, props.fipTicket))
    setTriedToSave(false)
    setDuplicateTicketNo('')
  }, [props.fipTicket, employee])

  useEffect(() => {
    // Set dates automatically when choosing FIPm - FIP moderasjonsbevis
    // FIPm is the only available option for family members, so also update when family member is selected
    if (state.ticketType?.value === '39' || state.ticketRightHolder.value !== employee.id) {
      if (!state.validFrom.value || !state.validTo.value) {
        const validFrom = state.validFrom.value ?? new Date()
        const validTo = state.validTo.value ?? getEndOfThisOrNextYear()

        setState({
          ...state,
          validFrom: { ...state.validFrom, value: validFrom },
          validTo: { ...state.validTo, value: validTo },
        })
      }
    }
  }, [state.ticketType, state.ticketRightHolder])

  useEffect(() => {
    // The personnel ticket office wants travel type to be automatically set to Business travel
    // when a 1st class ticket type is selected, since such vouchers are only issued for business travel.
    // Reset back to unselected when a non-1st-class ticket type is chosen afterwards.
    if (props.fipTicket === undefined) {
      const code = selectedTicketTypeCode(state)
      const travelType = isFirstClass(code) ? TJENESTEREISE : ''
      setState((prev) =>
        prev.travelType.value === travelType
          ? prev
          : { ...prev, travelType: { ...prev.travelType, value: travelType } }
      )
    }
  }, [state.ticketType])

  useEffect(() => {
    // The personnel ticket office wants "Valid until" to be automatically filled 3 months after
    // "Valid from" for 2nd class tickets, 2 months for DSB, when "Valid from" is changed.
    if (props.fipTicket === undefined) {
      const code = selectedTicketTypeCode(state)
      if (isSecondClass(code) && state.validFrom.value) {
        const validTo = addMonths(state.validFrom.value, isDsb(code) ? 2 : 3)
        setState((prev) =>
          prev.validTo.value?.getTime() === validTo.getTime()
            ? prev
            : { ...prev, validTo: { ...prev.validTo, value: validTo, variant: '', feedback: '' } }
        )
      }
    }
  }, [state.validFrom.value, state.ticketType])

  const abort = () => {
    setState(initialState(employee))
    props.close()
  }

  const [postResponse, doPost] = useApi(postFIPTickets, {
    successCallback: (data: FipTicket) => {
      props.close(data)
      setState(initialState(employee))
      addToast('FIP-kupong er lagt til!')
      setTriedToSave(false)
    },
  })

  const [patchResponse, doPatch] = useApi(patchFIPTickets, {
    successCallback: (data: FipTicket) => {
      props.close(data)
      setState(initialState(employee))
      addToast('Kupong er endret')
      setTriedToSave(false)
    },
  })

  const validateAllAndFetch = (callback: () => void) => {
    const localState = validateAllFields(state)
    let localDuplicateTicketNo = duplicateTicketNo

    const isDuplicateTicketNo =
      props.fipTicket === undefined &&
      props.fipTickets !== undefined &&
      props.fipTickets.some(
        (ticket) => !ticket.isDeleted && ticket.ticketNo?.toString() === localState.ticketNo.value
      )

    if (isDuplicateTicketNo && localDuplicateTicketNo !== 'closed') {
      localDuplicateTicketNo = 'error'
      setDuplicateTicketNo(localDuplicateTicketNo)
    } else {
      localDuplicateTicketNo = ''
      setDuplicateTicketNo(localDuplicateTicketNo)
    }

    if (formIsValid(localState) && (localDuplicateTicketNo === 'closed' || localDuplicateTicketNo === '')) {
      callback()
    }
    setState(localState)
    setTriedToSave(true)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (state.ticketRightHolder.value !== employee.id) {
      state.ticketType.value = '39'
    }
    validateAllAndFetch(() => {
      if (props.fipTicket) {
        doPatch({
          id: `${props.fipTicket?.ticketId}-${employee.id}`,
          body: body(state, employee.id),
        })
      } else {
        doPost({
          id: employee.id,
          body: body(state, employee.id),
        })
      }
    })
  }

  const responseError = postResponse.status === FetchState.ERROR || patchResponse.status === FetchState.ERROR
  const isLoading = postResponse.status === FetchState.LOADING || patchResponse.status === FetchState.LOADING
  const notStarted =
    postResponse.status === FetchState.NOT_STARTED && patchResponse.status === FetchState.NOT_STARTED
  const isEditMode = props.fipTicket !== undefined

  return (
    <Modal
      className="fip-modal"
      data-cy="fip-modal"
      open={props.open}
      title={isEditMode ? 'Endre kupong' : 'Ny kupong'}
      size="large"
      onDismiss={abort}
    >
      <form onSubmit={submit}>
        <GridContainer spacing="medium" rowSpacing="large" className="spacing-top-medium">
          <GridItem small={12} medium={6} data-cy="fip-modal-ticket-right-holder">
            <TicketRightHoldersDropDown
              formField={state.ticketRightHolder}
              disabled={isEditMode}
              update={(value) =>
                setState({
                  ...state,
                  ticketRightHolder: value,
                })
              }
            />
          </GridItem>
          <GridItem small={12} medium={6} data-cy="fip-modal-ticket-type">
            {state.ticketRightHolder.value === employee.id ? (
              <FIPTicketTypeDropdown
                field={state.ticketType}
                disabled={isEditMode}
                update={(value) =>
                  setState({
                    ...state,
                    ticketType: value,
                  })
                }
              />
            ) : (
              <TextField
                label={state.ticketType.displayName}
                name={state.ticketType.fieldName}
                value="FIPm - FIP moderasjonsbevis"
                readOnly
              />
            )}
          </GridItem>

          <GridItem small={12} medium={3}>
            {props.fipTicket === undefined ? (
              <ValidatableDatePicker
                date={state.issueDate}
                update={(value) =>
                  setState({
                    ...state,
                    issueDate: value,
                  })
                }
              />
            ) : (
              <TextField
                label={state.issueDate.displayName}
                disabled
                value={state.issueDate.value && toPrintFormat(state.issueDate.value)}
              />
            )}
          </GridItem>

          <GridItem small={12} medium={3}>
            <ValidatableTextField
              field={state.ticketNo}
              disabled={isEditMode}
              update={(value) =>
                setState({
                  ...state,
                  ticketNo: value,
                })
              }
            />
          </GridItem>

          <GridItem small={12} medium={6} data-cy="fip-modal-travel-type">
            <Dropdown
              variant={state.travelType.variant as VariantType}
              feedback={state.travelType.feedback}
              style={{ cursor: 'pointer' }}
              label={state.travelType.displayName}
              items={travelTypeItems}
              selectedItem={travelTypeItems.find((item) => item.value === state.travelType.value) ?? null}
              disabled={isEditMode}
              onChange={(e) => {
                if (e) {
                  setState({
                    ...state,
                    travelType: {
                      ...state.travelType,
                      value: e.value as string,
                    },
                  })
                }
              }}
            />
          </GridItem>

          <GridItem small={12} medium={3}>
            <ValidatableDatePicker
              date={state.validFrom}
              update={(value) =>
                setState({
                  ...state,
                  validFrom: value,
                })
              }
            />
          </GridItem>

          <GridItem small={12} medium={3}>
            <ValidatableDatePicker
              date={state.validTo}
              update={(value) =>
                setState({
                  ...state,
                  validTo: value,
                })
              }
            />
          </GridItem>

          <GridItem small={12} medium={6}>
            <ValidatableTextField
              field={state.note}
              disabled={isEditMode}
              update={(value) => {
                setState({
                  ...state,
                  note: {
                    ...state.note,
                    value: value.value,
                  },
                })
              }}
            />
          </GridItem>

          <GridItem small={12} style={{ marginTop: !formIsValid(state) ? 0 : space.medium }}>
            {triedToSave && !formIsValid(state) && (
              <SmallAlertBox variant="error" width="fit-content" style={{ marginBottom: space.large }}>
                Alle felt må være fylt ut med gyldige verdier for å sende inn skjemaet.
              </SmallAlertBox>
            )}
            {triedToSave && duplicateTicketNo === 'error' && (
              <BannerAlertBox
                data-cy="duplicate-ticket-alert"
                variant="error"
                width="fit-content"
                style={{ marginBottom: space.large }}
                onClose={() => setDuplicateTicketNo('closed')}
                closable
              >
                Billettnummeret er allerede registrert.
              </BannerAlertBox>
            )}

            {responseError && (
              <BannerAlertBox
                className="spacing-bottom-large"
                variant="error"
                title="Lagring feilet"
                closable={false}
                style={{ marginBottom: space.large }}
              >
                Prøv på nytt eller ta kontakt med Entur support om det vedvarer.
              </BannerAlertBox>
            )}

            <PrimaryButton
              className="spacing-right-medium"
              type="submit"
              loading={isLoading}
              disabled={triedToSave && !notStarted}
            >
              Lagre kupong
            </PrimaryButton>
            <SecondaryButton data-cy="fip-modal-cancel-button" onClick={abort}>
              Avbryt kupong
            </SecondaryButton>
          </GridItem>
        </GridContainer>
      </form>
    </Modal>
  )
}

export default FipModal
